import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  Button,
  TextInput,
  OtpInput,
  Text
} from 'common/components';
import { AuthProvider } from 'providers';
import {
  DataStore,
  FontFamilies,
  FontSizes,
  Theme,
  Logger,
  MediaQueries,
  Colors,
  isValidEmail,
  isValidPhoneNumber,
  formatPhoneNumber,
  formatPhoneNumberDisplay,
  isValidProviderResult
} from 'common';
import { withCommon } from 'common/hocs';

const RESEND_WAIT_SECONDS = 30;

const isOesenvlamfeesHost =
  typeof window !== 'undefined' &&
  window.location.hostname.includes('oesenvlamfees');

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  max-width: 400px;
  height: 100%;
  min-height: 480px;
  padding: 15px 15px 50px 15px;

  @media only screen and ${MediaQueries.Md} {
    min-height: 600px;
    padding: 0 0 80px 0;
  }
`;

const ErrorText = styled(Text)`
  color: #c10101;
  font-size: ${FontSizes.Smaller};
`;

const ResendContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
`;

const Instruction = styled(Text)`
  line-height: 1.55;
  max-width: 360px;
`;

const ResendText = styled(Text)`
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  color: ${Theme.Text.Color};
  text-decoration: underline;
`;

const ResendMessage = styled(Text)`
  color: ${Theme.Text.Color};
  font-size: ${FontSizes.Smaller};
`;

const DeliveryMethodContainer = styled.div`
  display: flex;
  margin: 0 0 15px 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${Theme.EventIdentity?.DividerColor || '#ddd'};
  width: 100%;
`;

const ToggleButton = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  background-color: ${(props) =>
    props.selected
      ? Colors.PrimaryOne
      : isOesenvlamfeesHost
        ? Colors.Transparent
        : Colors.White} !important;
  color: ${(props) =>
    props.selected
      ? Colors.White
      : isOesenvlamfeesHost
        ? Colors.White
        : Colors.Black} !important;
  font-size: ${FontSizes.Small};
  font-family: ${FontFamilies.Regular};
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: ${(props) =>
    props.isLast ? 'none' : `1px solid ${Theme.EventIdentity?.DividerColor || '#ddd'}`};

  &:hover {
    background-color: ${(props) =>
      props.selected
        ? Colors.PrimaryOne
        : isOesenvlamfeesHost
          ? Colors.Transparent
          : Colors.White};
  }

  &:focus {
    outline: none;
  }
`;

const OtpLoginComponent = ({ common }) => {
  // Authentication methods configuration
  const AUTH_CONFIG = {
    email: true,      // Enable/disable email authentication
    whatsapp: true,   // Enable/disable WhatsApp authentication
  };

  // Get available authentication methods
  const getAvailableMethods = () => {
    const methods = [];
    if (AUTH_CONFIG.email) methods.push('email');
    if (AUTH_CONFIG.whatsapp) methods.push('whatsapp');
    return methods;
  };

  // Get the default delivery method based on config
  const getDefaultDeliveryMethod = () => {
    const availableMethods = getAvailableMethods();
    if (availableMethods.length === 0) {
      console.warn('No authentication methods enabled in AUTH_CONFIG');
      return 'email'; // fallback
    }
    return availableMethods[0]; // Use the first available method as default
  };

  // Get dynamic instruction text based on enabled methods
  const getInstructionText = () => {
    const availableMethods = getAvailableMethods();
    
    if (availableMethods.length === 0) {
      return 'Please enter your contact information';
    }
    
    if (availableMethods.length === 1) {
      if (availableMethods[0] === 'email') {
        return 'Enter the email address you used to buy your tickets.';
      } else {
        return 'Enter the WhatsApp number you used to buy your tickets.';
      }
    }
    
    return 'Enter the email or WhatsApp number you used to buy your tickets.';
  };

  const [state, setState] = useState({
    currentView: 'contact', // 'contact' or 'otp'
    email: '',
    phone: '',
    otp: '',
    error: '',
    guid: '',
    deliveryMethod: getDefaultDeliveryMethod()
  });

  const [resendState, setResendState] = useState({
    countdown: 0,
    message: '',
  });

  const onEmailChange = (val) => {
    setState(prev => ({
      ...prev,
      email: val?.trim(),
      error: ''
    }));
  };

  const onPhoneChange = (val) => {
    setState(prev => ({
      ...prev,
      phone: formatPhoneNumberDisplay(val),
      error: ''
    }));
  };

  const onDeliveryMethodChange = (method) => {
    // Only allow changing to enabled methods
    const availableMethods = getAvailableMethods();
    if (!availableMethods.includes(method)) {
      console.warn(`Authentication method '${method}' is not enabled in AUTH_CONFIG`);
      return;
    }
    
    setState(prev => ({
      ...prev,
      deliveryMethod: method,
      error: ''
    }));
  };

  const onChangeContact = () => {
    setState(prev => ({
      ...prev,
      currentView: 'contact',
      otp: '',
      error: ''
    }));
    setResendState({
      countdown: 0,
      message: ''
    });
  };

  const startResendCountdown = (message = '') => {
    setResendState({
      countdown: RESEND_WAIT_SECONDS,
      message
    });
  };

  const onOtpChange = (value) => {
    setState(prev => ({
      ...prev,
      otp: value,
      error: ''
    }));
  };

  const validateContactInfo = () => {
    if (state.deliveryMethod === 'email') {
      if (!isValidEmail(state.email)) {
        setState(prev => ({
          ...prev,
          error: 'Please enter a valid email address'
        }));
        return false;
      }
      return true;
    } else if (state.deliveryMethod === 'whatsapp') {
      if (!isValidPhoneNumber(state.phone)) {
        setState(prev => ({
          ...prev,
          error: 'Please enter a valid phone number'
        }));
        return false;
      }
      return true;
    }
    return false;
  };

  const getContactValue = () => {
    return state.deliveryMethod === 'email' ? state.email : state.phone;
  };

  const getContactDisplay = () => {
    const contact = getContactValue();
    if (state.deliveryMethod === 'whatsapp') {
      return `WhatsApp: ${formatPhoneNumberDisplay(contact)}`;
    }
    return contact;
  };

  const sendOtp = async () => {
    if (!validateContactInfo()) {
      return;
    }

    setState(prev => ({ ...prev, error: '' }));

    try {
      common.showLoading();
      const contact = state.deliveryMethod === 'email' ? state.email : formatPhoneNumber(state.phone);
      const response = await AuthProvider.requestCode(contact);

      if (!isValidProviderResult(response)) {
        throw new Error(response.message);
      }

      setState(prev => ({
        ...prev,
        currentView: 'otp',
        guid: response.data.guid
      }));
      startResendCountdown();
    } catch (error) {
      Logger.error('OtpLoginComponent', 'sendOtp', error);
      common.showToast('Failed to send the code. Please try again.', 'error');
    } finally {
      common.hideLoading();
    }
  };

  const verifyOtp = async () => {
    if (!state.otp || state.otp.length !== 4) {
      setState(prev => ({
        ...prev,
        error: 'Please enter the 4-digit code'
      }));
      return;
    }

    setState(prev => ({ ...prev, error: '' }));

    try {
      common.showLoading();
      const response = await AuthProvider.submitCode(state.guid, state.otp, false);

      if (!isValidProviderResult(response)) {
        throw new Error(response.message);
      }

      if (!response.data.verified) {
        common.showToast('That code is incorrect. Check it and try again.', 'error');
        return;
      }

      DataStore.set('ACCESS_TOKEN', response.data.token);
      // Store the contact info based on delivery method
      if (state.deliveryMethod === 'email') {
        DataStore.set('LOGGED_IN_EMAIL', state.email);
      } else {
        // For WhatsApp, we still use LOGGED_IN_EMAIL key but store phone as identifier
        DataStore.set('LOGGED_IN_EMAIL', state.phone);
      }
      
      common.navigate('/profile/view-my-tickets');
    } catch (error) {
      Logger.error('OtpLoginComponent', 'verifyOtp', error);
      common.showToast('That code is incorrect. Please try again.', 'error');
    } finally {
      common.hideLoading();
    }
  };

  const resendOtp = async () => {
    if (resendState.countdown > 0) return;
    setState(prev => ({ ...prev, error: '' }));
    
    try {
      common.showLoading();
      const response = await AuthProvider.resendCode(state.guid);
      if (!isValidProviderResult(response)) {
        throw new Error(response.message);
      }

      const deliveryText = state.deliveryMethod === 'whatsapp' ? 'WhatsApp' : 'email';
      startResendCountdown(`A new code has been sent to your ${deliveryText}`);

      setTimeout(() => {
        setResendState(prev => ({
          ...prev,
          message: ''
        }));
      }, 5000);

    } catch (error) {
      Logger.error('OtpLoginComponent', 'resendOtp', error);
      common.showToast('Failed to resend the code. Please try again.', 'error');
    } finally {
      common.hideLoading();
    }
  };

  useEffect(() => {
    let timer;
    if (resendState.countdown > 0) {
      timer = setInterval(() => {
        setResendState(prev => ({
          ...prev,
          countdown: prev.countdown - 1
        }));
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [resendState.countdown]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const onBackClick = () => {
    if (state.currentView === 'otp') {
      onChangeContact();
      return;
    }

    common.navigate('/');
  };

  return (
    <>
    <Container>
      <Content>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
          <Text
            width={'90px'}
            display={'block'}
            padding={'0 0 12px 0'}
            fontSize={FontSizes.Small}
            color={Theme.Text.Color}
            textAlign={'left'}
            onClick={onBackClick}>
              ← Back
          </Text>
        </div>
        {state.currentView === 'contact' ? (
          <>
            <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Bigger} textAlign={'center'} padding={'0 0 8px 0'}>
              Find my tickets
            </Text>
            <Instruction fontSize={FontSizes.Small} textAlign={'center'} padding={'0 0 8px 0'}>
              {getInstructionText()}
            </Instruction>

            {/* Only show delivery method selection if multiple methods are available */}
            {getAvailableMethods().length > 1 && (
              <DeliveryMethodContainer>
                {AUTH_CONFIG.email && (
                  <ToggleButton 
                    selected={state.deliveryMethod === 'email'} 
                    onClick={() => onDeliveryMethodChange('email')}
                    isLast={!AUTH_CONFIG.whatsapp}
                  >
                    Email
                  </ToggleButton>
                )}
                
                {AUTH_CONFIG.whatsapp && (
                  <ToggleButton 
                    selected={state.deliveryMethod === 'whatsapp'} 
                    onClick={() => onDeliveryMethodChange('whatsapp')}
                    isLast={true}
                  >
                    WhatsApp
                  </ToggleButton>
                )}
              </DeliveryMethodContainer>
            )}

            {(state.deliveryMethod === 'email' && AUTH_CONFIG.email) && (
              <TextInput
                type="email"
                placeholder="name@email.com"
                value={state.email}
                onChange={onEmailChange}
                width="100%"
              />
            )}

            {(state.deliveryMethod === 'whatsapp' && AUTH_CONFIG.whatsapp) && (
              <TextInput
                type="tel"
                placeholder="082 000 0000"
                value={state.phone}
                onChange={onPhoneChange}
                width="100%"
              />
            )}

            {state.error && <ErrorText>{state.error}</ErrorText>}

            <Button
              text="Send code"
              onClick={sendOtp}
              width="100%"
              margin={'6px 0 0 0'}
            />
          </>
        ) : (
          <>
            <Text fontFamily={FontFamilies.Bold} fontSize={FontSizes.Bigger} textAlign={'center'} padding={'0 0 8px 0'}>
              Enter your code
            </Text>

            <Instruction fontSize={FontSizes.Small} textAlign={'center'} padding={'0 0 8px 0'}>
              We sent a 4-digit code to <span style={{ fontFamily: FontFamilies.Bold }}>{getContactDisplay()}</span>. Enter it to view and download your tickets.
            </Instruction>

            <OtpInput
              value={state.otp}
              onChange={onOtpChange}
              numInputs={4}
              separator={<span style={{ width: '8px' }}></span>}
              isInputNum
              shouldAutoFocus
            />

            {state.error && <ErrorText>{state.error}</ErrorText>}

            <Button
              text="Verify code"
              onClick={verifyOtp}
              width="100%"
              margin={'10px 0'}
            />

            <ResendContainer>
              <ResendText
                onClick={resendOtp}
                disabled={resendState.countdown > 0}
                fontSize={FontSizes.Small}
              >
                {resendState.countdown > 0
                  ? `Resend code in ${resendState.countdown}s`
                  : 'Resend code'}
              </ResendText>
              <ResendText
                onClick={onChangeContact}
                fontSize={FontSizes.Small}
              >
                Use a different email or number
              </ResendText>
              
              {resendState.message && (
                <ResendMessage>
                  {resendState.message}
                </ResendMessage>
              )}
            </ResendContainer>
          </>
        )}
      </Content>
    </Container>
    </>
  );
};

export const OtpLogin = withCommon(OtpLoginComponent, {
  showLoadingOnLoad: false
});